import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ASSETS, SCHOOLS } from '@/lib/api';
import { Crest } from '@/components/Brand';
import { ArrowRight, CheckCircle2, Award, BookOpen, Briefcase, GraduationCap, Scale, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TRJServicesCTA from '@/components/TRJServicesCTA';
import SafeImage from '@/components/SafeImage';

const ICONS = { Scale, BookOpen, Briefcase, GraduationCap, Award };

const VALUES = [
  { k: 'Knowledge', d: 'Deep, practical expertise rooted in real-world practice.' },
  { k: 'Integrity', d: 'Ethical standards that uphold the profession.' },
  { k: 'Leadership', d: 'Equipping graduates to lead and serve clients.' },
  { k: 'Excellence', d: 'Curricula crafted to professional benchmarks.' },
  { k: 'Accountability', d: 'Outcomes you can measure, certificates you can verify.' },
  { k: 'Service', d: 'A community that supports every learner, end-to-end.' },
];

export default function Home() {
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    api.get('/programs').then(({ data }) => setPrograms(data.filter(p => !p.is_coming_soon)));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0B1F3A] text-white">
        <div className="absolute inset-0 hero-grain opacity-60 pointer-events-none" />
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-32 w-[420px] h-[420px] rounded-full bg-gold/5 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <span className="trj-divider">Established Excellence</span>
            <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-6">
              The institution for the next generation of <span className="text-gold font-serif italic">tax & financial</span> professionals.
            </h1>
            <p className="mt-6 text-base lg:text-lg text-white/80 max-w-2xl leading-relaxed">
              TRJ Academy delivers professional education, certification, and career advancement for taxation,
              accounting, bookkeeping, and business development — built around outcomes, not theory.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/programs" data-testid="hero-cta-programs">
                <Button className="btn-gold h-12 px-7 text-base">Explore Programs <ArrowRight className="ml-2 h-4 w-4" /></Button>
              </Link>
              <Link to="/programs" data-testid="hero-cta-apply">
                <Button variant="outline" className="h-12 px-7 text-base border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-gold">
                  Enroll in a Program
                </Button>
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
              <div><div className="font-serif text-3xl text-gold">5</div><div className="text-xs uppercase tracking-widest text-white/60 mt-1">Schools</div></div>
              <div><div className="font-serif text-3xl text-gold">12</div><div className="text-xs uppercase tracking-widest text-white/60 mt-1">Programs</div></div>
              <div><div className="font-serif text-3xl text-gold">100%</div><div className="text-xs uppercase tracking-widest text-white/60 mt-1">Online</div></div>
            </div>
          </div>
          <div className="lg:col-span-5 animate-fade-up delay-200">
            <div className="relative crest-glow">
              <img src={ASSETS.shield} alt="TRJ Academy crest" loading="eager" decoding="async" className="max-w-md mx-auto" />
            </div>
            <div className="mt-6 text-center">
              <div className="font-serif italic text-gold/90 text-xl">— Learn. Certify. Advance. —</div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT STRIP */}
      <section className="py-20 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="trj-divider !text-navy/70 !before:bg-navy/30 !after:bg-navy/30">About the Academy</span>
            <h2 className="font-display font-semibold text-3xl lg:text-4xl text-navy mt-5 leading-tight">
              An institution built for practitioners — by practitioners.
            </h2>
          </div>
          <div className="lg:col-span-7 text-slate-600 leading-relaxed">
            <p className="text-base">
              Operated by <strong className="text-navy">TRJ Services, Inc.</strong>, TRJ Academy was founded to
              raise the standard of professional training in taxation and accounting. Every program is engineered around
              practical application — the skills you need to perform, certify, and grow in the field.
            </p>
            <p className="mt-4 text-base">
              From your first preparer fundamentals course to advanced certification, TRJ Academy is the seal of
              practical excellence on your professional journey.
            </p>
            <Link to="/about" className="inline-flex items-center mt-6 text-navy font-semibold hover:text-gold" data-testid="about-learn-more">
              Learn more <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SCHOOLS GRID */}
      <section className="py-24 bg-trjlight">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="trj-divider">The Schools</span>
            <h2 className="font-display font-semibold text-3xl lg:text-4xl text-navy mt-4">Five schools. One standard of excellence.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SCHOOLS.map((s, i) => {
              const Icon = ICONS[s.icon] || BookOpen;
              return (
                <Link key={s.slug} to={`/schools#${s.slug}`} data-testid={`school-card-${s.slug}`}
                  className="card-elevate group bg-white border border-slate-200 rounded-xl p-8">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-lg bg-navy/5 text-navy flex items-center justify-center group-hover:bg-gold/15 group-hover:text-gold transition-colors">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-gold font-semibold">School {String(i + 1).padStart(2, '0')}</div>
                      <h3 className="font-display font-semibold text-lg text-navy mt-1">{s.name}</h3>
                    </div>
                  </div>
                  <p className="mt-5 text-sm text-slate-600 leading-relaxed">{s.tagline}</p>
                  <div className="mt-5 inline-flex items-center text-sm font-semibold text-navy group-hover:text-gold">
                    Explore <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <span className="trj-divider">Featured Programs</span>
              <h2 className="font-display font-semibold text-3xl lg:text-4xl text-navy mt-4">Launching your career, this season.</h2>
            </div>
            <Link to="/programs" className="text-navy font-semibold inline-flex items-center hover:text-gold" data-testid="view-all-programs">
              View all programs <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {programs.slice(0, 3).map((p) => (
              <Link key={p.program_id} to={`/programs/${p.slug}`} data-testid={`program-card-${p.slug}`}
                className="card-elevate group bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col">
                <div className="aspect-[16/10] overflow-hidden bg-navy/5">
                  <SafeImage src={p.image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="text-xs uppercase tracking-widest text-gold font-semibold">{p.school}</div>
                  <h3 className="font-display font-semibold text-xl text-navy mt-2 leading-snug">{p.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{p.short_description}</p>
                  <div className="mt-6 flex items-center justify-between pt-4 border-t border-slate-100">
                    <div className="font-serif text-2xl text-navy">${p.price_usd.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                    <span className="text-sm font-semibold text-navy group-hover:text-gold inline-flex items-center">Details <ArrowRight className="ml-1 h-4 w-4" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 bg-[#0B1F3A] text-white relative overflow-hidden">
        <div className="absolute inset-0 hero-grain opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <span className="trj-divider">Our Values</span>
              <h2 className="font-display font-semibold text-3xl lg:text-4xl mt-5 leading-tight">
                Built on six principles that <span className="text-gold font-serif italic">never bend</span>.
              </h2>
              <p className="mt-6 text-white/70 leading-relaxed max-w-md">
                These values shape every program we publish and every graduate we credential. They are non-negotiable.
              </p>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {VALUES.map((v, i) => (
                <div key={v.k} className="bg-white/[0.04] border border-white/10 rounded-lg p-5 hover:border-gold/40 transition-colors">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-gold" />
                    <div className="font-display font-semibold text-white">{v.k}</div>
                  </div>
                  <p className="text-sm text-white/70 mt-2">{v.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STUDENT SUCCESS */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <img src={ASSETS.graduation} alt="Graduates" loading="lazy" decoding="async" className="rounded-2xl shadow-xl" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          </div>
          <div>
            <span className="trj-divider">Student Success</span>
            <h2 className="font-display font-semibold text-3xl lg:text-4xl text-navy mt-5 leading-tight">
              A certificate earned at TRJ Academy is a credential — not a token.
            </h2>
            <ul className="mt-8 space-y-4">
              {[
                'Unique certificate number (TRJA-YYYY-NNNNNN) with QR verification',
                'Lifetime certificate archive in your student dashboard',
                'Practical assessments mapped to industry standards',
                'Career pathway opportunities with TRJ Services, Inc. & partners',
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-trjgreen mt-0.5" />
                  <span className="text-slate-700">{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <Link to="/admissions" data-testid="success-cta">
                <Button className="btn-navy h-12 px-7 text-base">Start Your Application</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-trjlight">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Crest size="lg" className="mx-auto" />
          <h2 className="font-display font-semibold text-3xl lg:text-4xl text-navy mt-6">
            Your career deserves the seal of <span className="text-gold font-serif italic">TRJ</span>.
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
            Apply today and join the next cohort of practitioners building careers that compound.
          </p>
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <Link to="/programs" data-testid="bottom-cta-apply"><Button className="btn-gold h-12 px-7">Enroll Now</Button></Link>
            <Link to="/contact" data-testid="bottom-cta-contact"><Button variant="outline" className="h-12 px-7 border-navy/20 text-navy hover:bg-navy hover:text-white">Speak with Admissions</Button></Link>
          </div>
        </div>
      </section>

      {/* Parent organization cross-promotion */}
      <TRJServicesCTA variant="dark" />
    </div>
  );
}
